import React, { createContext, useContext, useState } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  isDashboardAuthenticated: boolean;
  authenticate: (password: string) => Promise<boolean>;
  authenticateDashboard: (passphrase: string) => Promise<boolean>;
  isLoading: boolean;
  dashboardPassphrase: string | null;
}

const API_URL = import.meta.env.VITE_API_URL || '';
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('wedding-auth') === 'authenticated';
  });
  const [isDashboardAuthenticated, setIsDashboardAuthenticated] = useState(() => {
    return !!sessionStorage.getItem('dashboard-auth');
  });
  const [dashboardPassphrase, setDashboardPassphrase] = useState<string | null>(() => {
    return sessionStorage.getItem('dashboard-auth');
  });
  const [isLoading, setIsLoading] = useState(false);

  const authenticate = async (password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();
      
      if (data.authenticated) {
        setIsAuthenticated(true);
        sessionStorage.setItem('wedding-auth', data.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Authentication error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const authenticateDashboard = async (passphrase: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      // The correct passphrase is hardcoded in the backend
      if (passphrase === 'showmewhatyougot') {
        setIsDashboardAuthenticated(true);
        setDashboardPassphrase(passphrase);
        sessionStorage.setItem('dashboard-auth', passphrase);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Dashboard authentication error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isDashboardAuthenticated,
      authenticate, 
      authenticateDashboard,
      isLoading,
      dashboardPassphrase
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
